import { useCallback, useEffect, useMemo, useRef } from 'react'

import { Dayjs } from 'dayjs'

import _dayjs from 'src/Helpers/dayjs'
import {
  getDefaultStartDate,
  getIsStartDateOutdated,
} from 'src/Constants/functions'
import { useHasPlan } from 'src/Hooks/useHasPlan'
import { useGetSubscriptionQuery } from 'src/Redux/subscription-process/subscriptionApi'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { useUpdateSpotifyCampaignStartDateMutation } from 'src/Redux/spotify-campaignApi'

type UseSpotifyScheduleStartDate = {
  startDate: Dayjs
  todayEnable: boolean
  onChangeDate: (date: Dayjs) => void
}

export const useSpotifyScheduleStartDate = (): UseSpotifyScheduleStartDate => {
  const { campaignDetails, setStartDate } = useSpotifyCampaignStatus()
  const [updateSpotifyCampaignStartDate] =
    useUpdateSpotifyCampaignStartDateMutation()

  const hasPlan = useHasPlan()
  const { isLoading: isSubscriptionLoading } = useGetSubscriptionQuery()

  const persistedDefaultCampaignIdRef = useRef<string | number | null>(null)

  const isStartDateOutdated = getIsStartDateOutdated(
    campaignDetails?.start_date,
    hasPlan,
  )

  useEffect(() => {
    if (isSubscriptionLoading || !campaignDetails) {
      return
    }
    if (
      (campaignDetails.start_date && !isStartDateOutdated) ||
      persistedDefaultCampaignIdRef.current === campaignDetails.id
    ) {
      return
    }
    persistedDefaultCampaignIdRef.current = campaignDetails.id

    const defaultDate = _dayjs(getDefaultStartDate(hasPlan))
    setStartDate(defaultDate.format())
    void updateSpotifyCampaignStartDate({
      id: String(campaignDetails.id),
      startDate: defaultDate.format('YYYY-MM-DD'),
    })
  }, [
    campaignDetails,
    hasPlan,
    isStartDateOutdated,
    isSubscriptionLoading,
    setStartDate,
    updateSpotifyCampaignStartDate,
  ])

  const onChangeDate = useCallback(
    (date: Dayjs) => {
      if (!campaignDetails) {
        return
      }
      setStartDate(date.format())
      void updateSpotifyCampaignStartDate({
        id: String(campaignDetails.id),
        startDate: date.format('YYYY-MM-DD'),
      })
    },
    [campaignDetails, setStartDate, updateSpotifyCampaignStartDate],
  )

  const persistedStartDate = campaignDetails?.start_date

  const startDate = useMemo(() => {
    if (persistedStartDate && !isStartDateOutdated) {
      return _dayjs(persistedStartDate)
    }
    return _dayjs(getDefaultStartDate(hasPlan))
  }, [hasPlan, isStartDateOutdated, persistedStartDate])

  return { startDate, onChangeDate, todayEnable: hasPlan }
}
